// Render selected resources
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Chip from "@mui/material/Chip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import GroupsIcon from "@mui/icons-material/Groups";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import GavelIcon from "@mui/icons-material/Gavel";
import Diversity3Icon from "@mui/icons-material/Diversity3";

import ExpandList from "../components/resource/ExpandList";
import EmailForm from "../components/resource/EmailForm";
import { categoryTransform } from "../constants/resourceType";

// Category configuration: icon, theme color key, and display order
const CATEGORY_CONFIG = {
  "Financial Resources": {
    icon: AttachMoneyIcon,
    colorMain: "categoryFinancial.main",
    colorLight: "categoryFinancial.light",
    rawMain: "#F9A825",
  },
  "Organizations for Survivors": {
    icon: GroupsIcon,
    colorMain: "categoryOrganizations.main",
    colorLight: "categoryOrganizations.light",
    rawMain: "#00897B",
  },
  "Medical Resources": {
    icon: LocalHospitalIcon,
    colorMain: "categoryMedical.main",
    colorLight: "categoryMedical.light",
    rawMain: "#1E88E5",
  },
  "Legal Resources": {
    icon: GavelIcon,
    colorMain: "categoryLegal.main",
    colorLight: "categoryLegal.light",
    rawMain: "#5C6BC0",
  },
  "Identity/Disability/Situational Resources": {
    icon: Diversity3Icon,
    colorMain: "categoryIdentity.main",
    colorLight: "categoryIdentity.light",
    rawMain: "#EF5350",
  },
};

const CATEGORY_ORDER = [
  "Financial Resources",
  "Organizations for Survivors",
  "Medical Resources",
  "Legal Resources",
  "Identity/Disability/Situational Resources",
];

// Framer-motion animation variants
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const Resources = () => {
  const location = useLocation();
  const [resources, setResources] = useState([]);
  const [category, setCategory] = useState({
    "Financial Resources": [],
    "Organizations for Survivors": [],
    "Medical Resources": [],
    "Legal Resources": [],
    "Identity/Disability/Situational Resources": [],
  });

  useEffect(() => {
    if (location.state?.resources) {
      const resourceArray = [];
      const categoryArray = {
        "Financial Resources": [],
        "Organizations for Survivors": [],
        "Medical Resources": [],
        "Legal Resources": [],
        "Identity/Disability/Situational Resources": [],
      };

      for (let i = 0; i < location.state.resources.length; i++) {
        const { resources } = location.state.resources[i];
        // original resource data
        resourceArray.push(...resources);

        // categorized data
        for (const resource of resources) {
          const { resourceTypeName } = resource;
          const categoryType = categoryTransform[resourceTypeName];
          if (categoryType && categoryArray[categoryType]) {
            categoryArray[categoryType].push(resource);
          }
        }
      }
      // set original resource data
      setResources(resourceArray);
      // set categorized data
      setCategory(categoryArray);
    }
  }, [location.state]);

  const formatEmailResources = (resources) => {
    let htmlMessage = "";
    resources.map((obj) => {
      const { resourceTypeName, name, link } = obj;
      htmlMessage += `
        <div style="display: block;">
          <h3 style="font-weight: bold">${resourceTypeName}</h3>
          <p>
          <div>${name}: <a href="${link}">Website</a></div>
          </p>
        </div>
      `;
      return null;
    });
    return htmlMessage;
  };

  // Count total visible categories
  const visibleCategories = CATEGORY_ORDER.filter(
    (cat) => category[cat].length > 0
  );
  const totalResources = resources.length;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        pb: 6,
      }}
    >
      {/* Hero Header */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        sx={{
          textAlign: "center",
          pt: { xs: 5, sm: 6, md: 8 },
          pb: { xs: 4, sm: 5, md: 6 },
          px: { xs: 2, sm: 3 },
          maxWidth: 800,
          mx: "auto",
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" },
            mb: 2,
            background: "linear-gradient(135deg, #7B1FA2 0%, #9C27B0 40%, #E040FB 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            letterSpacing: "-0.02em",
          }}
        >
          Your Resources
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.125rem" },
            lineHeight: 1.7,
            maxWidth: 620,
            mx: "auto",
            mb: 2,
          }}
        >
          Every survivor's story is unique. Your resource list reflects that.
          Below are all the resources you are eligible for. Explore each category
          to learn more about the support available to you.
        </Typography>
        {totalResources > 0 && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2.5,
              py: 0.75,
              borderRadius: "20px",
              backgroundColor: "rgba(123, 31, 162, 0.06)",
              border: "1px solid rgba(123, 31, 162, 0.1)",
            }}
          >
            <Typography
              sx={{
                color: "primary.main",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              {totalResources} resource{totalResources !== 1 ? "s" : ""} across{" "}
              {visibleCategories.length} categor
              {visibleCategories.length !== 1 ? "ies" : "y"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Category Accordions */}
      <Box
        component={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        sx={{
          maxWidth: 860,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {CATEGORY_ORDER.map((categoryName) => {
          if (category[categoryName].length === 0) return null;

          const config = CATEGORY_CONFIG[categoryName];
          const IconComponent = config.icon;
          const count = category[categoryName].length;

          return (
            <Box
              component={motion.div}
              variants={itemVariants}
              key={categoryName}
            >
              <Accordion
                sx={{
                  "&.MuiAccordion-root": {
                    backgroundColor: "background.paper",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon sx={{ color: "text.secondary" }} />
                  }
                  aria-controls={`${categoryName}-content`}
                  id={`${categoryName}-header`}
                  sx={{
                    py: { xs: 0.5, sm: 1 },
                    px: { xs: 2, sm: 3 },
                    "&:hover": {
                      backgroundColor: "rgba(123, 31, 162, 0.02)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 1.5, sm: 2 },
                      width: "100%",
                      pr: 1,
                    }}
                  >
                    {/* Category icon in colored circle */}
                    <Box
                      sx={{
                        width: { xs: 36, sm: 42 },
                        height: { xs: 36, sm: 42 },
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: config.colorLight,
                        flexShrink: 0,
                        transition: "transform 0.2s ease",
                        ".MuiAccordionSummary-root:hover &": {
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      <IconComponent
                        sx={{
                          fontSize: { xs: 18, sm: 22 },
                          color: config.colorMain,
                        }}
                      />
                    </Box>

                    {/* Category title */}
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.125rem" },
                        color: "text.primary",
                        flex: 1,
                        lineHeight: 1.3,
                      }}
                    >
                      {categoryName}
                    </Typography>

                    {/* Resource count badge */}
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        height: 24,
                        minWidth: 32,
                        borderRadius: "12px",
                        backgroundColor: `${config.rawMain}14`,
                        color: config.colorMain,
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        border: `1px solid ${config.rawMain}25`,
                        "& .MuiChip-label": {
                          px: 1,
                        },
                      }}
                    />
                  </Box>
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    px: { xs: 2, sm: 3 },
                    pb: 2.5,
                  }}
                >
                  <ExpandList resource={category[categoryName]} />
                </AccordionDetails>
              </Accordion>
            </Box>
          );
        })}
      </Box>

      {/* Email Form Section */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        sx={{
          maxWidth: 860,
          mx: "auto",
          px: { xs: 2, sm: 3 },
        }}
      >
        <EmailForm message={formatEmailResources(resources)} />
      </Box>
    </Box>
  );
};

export default Resources;
